/**
 * 3x3 median filter. Run *before* CLAHE/illumination flattening on the
 * low-light path: a contrast-limited equalizer is, by construction, a noise
 * amplifier — it stretches whatever local contrast it finds, including
 * salt-and-pepper sensor noise, and the unsharp pass afterwards then turns
 * that amplified noise into crisp fake edges. Left unchecked, that
 * manufactured edge structure can occasionally satisfy a linear symbology's
 * checksum on pure noise (verified empirically against zxing-wasm: heavy
 * synthetic noise fed through CLAHE + unsharp produced phantom GS1 DataBar
 * reads). A median filter removes salt-and-pepper outliers while leaving
 * real bar/module edges intact, which a mean/box blur would soften instead.
 */
export function medianDenoise3x3(luma: Uint8ClampedArray, width: number, height: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(luma.length);
  const window = new Uint8ClampedArray(9);
  for (let y = 0; y < height; y++) {
    const yUp = Math.max(0, y - 1) * width;
    const yMid = y * width;
    const yDown = Math.min(height - 1, y + 1) * width;
    for (let x = 0; x < width; x++) {
      const xL = Math.max(0, x - 1);
      const xR = Math.min(width - 1, x + 1);
      window[0] = luma[yUp + xL];
      window[1] = luma[yUp + x];
      window[2] = luma[yUp + xR];
      window[3] = luma[yMid + xL];
      window[4] = luma[yMid + x];
      window[5] = luma[yMid + xR];
      window[6] = luma[yDown + xL];
      window[7] = luma[yDown + x];
      window[8] = luma[yDown + xR];
      window.sort();
      out[yMid + x] = window[4];
    }
  }
  return out;
}
